// Relances et impayés (F-053, F-060) : relances graduées validées avant envoi, arrêt au paiement, dossier d'impayé,
// échéancier amiable suivi séparément, export pour les intervenants choisis par le bailleur.
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money, today } from "@core/format";
import { Badge, Button, ButtonLink, Card, Checkbox, Empty, ErrorBox, Input, KeyValues, Loading, Modal, Notice, PageHeader, Select, Table, Tabs, Textarea } from "@core/ui";
import { Amount, CallStatus, CoverageStatus, LeaseSelect, useAction, useG, useLeases } from "./shared";

const KIND_LABELS: Record<string, string> = { rappel_amiable: "Rappel amiable", relance: "Relance", mise_en_demeure: "Mise en demeure" };
const R_STATUS: Record<string, { label: string; tone: "neutre" | "encre" | "succes" | "attention" | "erreur" }> = {
  a_valider: { label: "À valider", tone: "attention" },
  envoyee: { label: "Envoyée", tone: "encre" },
  preparee: { label: "Courrier préparé", tone: "encre" },
  arretee: { label: "Arrêtée", tone: "succes" },
  annulee: { label: "Annulée", tone: "neutre" },
};

type Reminder = {
  id: string;
  lease_id: string;
  rent_call_id: string;
  step: number;
  kind: string;
  channel: string;
  status: string;
  subject: string;
  body: string;
  outstanding_cents: number;
  scheduled_on: string;
  sent_at: string | null;
  letter_id: string | null;
  stopped_reason: string;
  created_at: string;
  period_start: string;
  period_end: string;
  due_on: string;
  property_label: string;
  tenants: string | null;
};

type RTab = "relances" | "impayes" | "assurances" | "sequence";

export function RelancesPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("onglet") as RTab | null) ?? "relances";
  const setTab = (t: RTab) => setParams(t === "relances" ? {} : { onglet: t }, { replace: true });
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Relances et impayés"
        intro="Les relances sont préparées une étape à la fois, relues puis envoyées par vous. Elles s’arrêtent dès que l’échéance est réglée. Aucune pénalité n’est ajoutée ; aucune procédure n’est engagée automatiquement."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "relances", label: "Relances" },
          { value: "impayes", label: "Dossiers d’impayé" },
          { value: "assurances", label: "Assurances" },
          { value: "sequence", label: "Séquence de relance" },
        ]}
      />
      {tab === "relances" ? <RemindersList /> : null}
      {tab === "impayes" ? <DebtsList /> : null}
      {tab === "assurances" ? <InsuranceOverview /> : null}
      {tab === "sequence" ? <SequenceSettings /> : null}
    </div>
  );
}

function RemindersList() {
  const { g, can, to } = useG();
  const q = useGet<{ items: Reminder[] }>(g("/reminders"));
  const { busy, run } = useAction();
  const [last, setLast] = useState<{ created: any[]; waiting: any[] } | null>(null);
  const [edit, setEdit] = useState<Reminder | null>(null);
  const pending = (q.data?.items ?? []).filter((r) => r.status === "a_valider");
  const history = (q.data?.items ?? []).filter((r) => r.status !== "a_valider");
  return (
    <>
      <Card
        title="Relances à valider"
        actions={
          can("operate") ? (
            <Button
              small
              busy={busy === "run"}
              onClick={async () => {
                const r = await run("run", () => post<any>(g("/reminders/run")), (x: any) => `${x.created.length} relance(s) préparée(s).`);
                if (r) setLast(r);
              }}
            >
              Préparer les relances dues
            </Button>
          ) : null
        }
      >
        {last && last.waiting.length ? (
          <details>
            <summary>{last.waiting.length} échéance(s) en retard sans nouvelle relance</summary>
            <ul>
              {last.waiting.map((w, i) => (
                <li key={i}>
                  {w.property_label} · {w.period_label} : {w.reason}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {pending.length === 0 ? <p className="muted">Aucune relance en attente de validation.</p> : null}
        <ul className="gestion-list">
          {pending.map((r) => (
            <li key={r.id}>
              <div className="gestion-line">
                <span>
                  <strong>{KIND_LABELS[r.kind] ?? r.kind}</strong> · {r.property_label} · échéance du {date(r.due_on)} · reste dû {money(r.outstanding_cents)}
                  <span className="block small muted">
                    {r.tenants} · canal : {r.channel === "email" ? "courriel" : r.channel === "recommande" ? "recommandé (préparé comme courrier)" : "courrier"}
                  </span>
                </span>
                {can("operate") ? (
                  <span className="gestion-actions">
                    <Button variant="discret" small onClick={() => setEdit(r)}>
                      Relire et modifier
                    </Button>
                    <Button small busy={busy === r.id} onClick={() => run(r.id, () => post<any>(g(`/reminders/${r.id}/send`)), (x: any) => x.message ?? "Relance envoyée (envoi simulé).")}>
                      {r.kind === "mise_en_demeure" || r.channel !== "email" ? "Préparer le courrier" : "Valider et envoyer"}
                    </Button>
                    <Button variant="discret" small busy={busy === `c${r.id}`} onClick={() => run(`c${r.id}`, () => post(g(`/reminders/${r.id}/cancel`)), "Relance annulée.")}>
                      Annuler
                    </Button>
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Journal des relances">
        {history.length === 0 ? <p className="muted">Aucune relance envoyée.</p> : null}
        <ul className="gestion-list">
          {history.map((r) => (
            <li key={r.id} className="gestion-line">
              <span>
                {KIND_LABELS[r.kind] ?? r.kind} · {r.property_label} · échéance du {date(r.due_on)}
                <span className="block small muted">
                  {r.sent_at ? `Envoyée le ${dateTime(r.sent_at)}` : `Créée le ${dateTime(r.created_at)}`}
                  {r.stopped_reason ? ` · ${r.stopped_reason}` : ""}
                </span>
              </span>
              <span className="gestion-actions">
                <Badge tone={R_STATUS[r.status]?.tone ?? "neutre"}>{R_STATUS[r.status]?.label ?? r.status}</Badge>
                {r.letter_id ? <Link to={to(`gestion/courriers?courrier=${r.letter_id}`)}>Voir le courrier</Link> : null}
              </span>
            </li>
          ))}
        </ul>
      </Card>
      {edit ? <EditReminder r={edit} onClose={() => setEdit(null)} /> : null}
    </>
  );
}

function EditReminder({ r, onClose }: { r: Reminder; onClose: () => void }) {
  const { g } = useG();
  const { busy, run } = useAction();
  const [subject, setSubject] = useState(r.subject);
  const [body, setBody] = useState(r.body);
  return (
    <Modal
      open
      onClose={onClose}
      title={`${KIND_LABELS[r.kind] ?? "Relance"} — relecture`}
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Fermer
          </Button>
          <Button
            busy={busy === "save"}
            onClick={async () => {
              const ok = await run("save", () => put(g(`/reminders/${r.id}`), { subject, body }), "Texte enregistré.");
              if (ok) onClose();
            }}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <Input label="Objet" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <Textarea label="Texte" rows={14} value={body} onChange={(e) => setBody(e.target.value)} help="Ton respectueux, montant exact et référence de paiement. Pas de menace ni de pénalité non prévue au bail." />
    </Modal>
  );
}

type Step = { step: number; kind: string; delay_days: number; channel: string; auto_send: boolean };

function SequenceSettings() {
  const { g, can } = useG();
  const q = useGet<{ reminder_steps: Step[]; match_amount_tolerance_cents: number; match_date_window_days: number }>(g("/settings"));
  const { busy, run, error } = useAction();
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [dateWindow, setDateWindow] = useState<string | null>(null);
  const current = steps ?? q.data?.reminder_steps ?? [];
  const upd = (i: number, patch: Partial<Step>) => setSteps(current.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  if (q.isLoading) return <Loading />;
  return (
    <Card title="Séquence de relance et rapprochement">
      <p className="muted">Délais comptés en jours après la date d’échéance. La mise en demeure est toujours relue et validée ; elle n’est jamais envoyée automatiquement.</p>
      {current.map((s, i) => (
        <div key={i} className="form-row">
          <Select label={`Étape ${s.step}`} value={s.kind} disabled={!can("operate")} onChange={(e) => upd(i, { kind: e.target.value, auto_send: e.target.value === "mise_en_demeure" ? false : s.auto_send })} options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))} />
          <Input label="Délai (jours)" type="number" min={1} max={365} value={s.delay_days} disabled={!can("operate")} onChange={(e) => upd(i, { delay_days: Number(e.target.value) })} />
          <Select
            label="Canal"
            value={s.channel}
            disabled={!can("operate")}
            onChange={(e) => upd(i, { channel: e.target.value })}
            options={s.kind === "mise_en_demeure" ? [{ value: "recommande", label: "Recommandé" }, { value: "courrier", label: "Courrier" }] : [{ value: "email", label: "Courriel" }, { value: "courrier", label: "Courrier" }]}
          />
          {s.kind !== "mise_en_demeure" ? <Checkbox label="Envoyer sans validation" checked={s.auto_send} onChange={(v) => can("operate") && upd(i, { auto_send: v })} /> : null}
        </div>
      ))}
      {can("operate") ? (
        <div className="gestion-actions">
          {current.length < 5 ? (
            <Button variant="discret" small onClick={() => setSteps([...current, { step: current.length + 1, kind: "relance", delay_days: (current[current.length - 1]?.delay_days ?? 0) + 10, channel: "email", auto_send: false }])}>
              Ajouter une étape
            </Button>
          ) : null}
          {current.length > 1 ? (
            <Button variant="discret" small onClick={() => setSteps(current.slice(0, -1))}>
              Retirer la dernière étape
            </Button>
          ) : null}
        </div>
      ) : null}
      <Input
        label="Fenêtre de dates pour le rapprochement (jours)"
        type="number"
        min={0}
        max={60}
        value={dateWindow ?? String(q.data?.match_date_window_days ?? 10)}
        disabled={!(can("operate") || can("finance"))}
        onChange={(e) => setDateWindow(e.target.value)}
        help="Une opération proche d’une échéance est signalée comme telle ; la validation reste manuelle."
      />
      <ErrorBox error={error} />
      {can("operate") || can("finance") ? (
        <Button
          busy={busy === "save"}
          onClick={() => run("save", () => put(g("/settings"), { reminder_steps: current, match_date_window_days: dateWindow === null ? undefined : Number(dateWindow) }), "Paramètres enregistrés.")}
        >
          Enregistrer
        </Button>
      ) : null}
    </Card>
  );
}

function DebtsList() {
  const { g, can, to } = useG();
  const q = useGet<{ items: any[]; closed: any[] }>(g("/debts"));
  const leases = useLeases();
  const { busy, run } = useAction();
  const [lease, setLease] = useState("");
  return (
    <>
      <Card title="Baux avec un reste dû échu">
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {q.data && q.data.items.length === 0 ? <Empty title="Aucun impayé">Toutes les échéances échues sont réglées.</Empty> : null}
        <ul className="gestion-list">
          {(q.data?.items ?? []).map((i) => (
            <li key={i.lease_id} className="gestion-line">
              <span>
                <strong>{i.property_label}</strong> · {i.tenants}
                <span className="block small">
                  Reste dû échu : <Amount cents={i.balance.overdue_cents} strong /> {i.balance.oldest_overdue_on ? `depuis le ${date(i.balance.oldest_overdue_on)}` : ""}
                </span>
              </span>
              {i.case ? (
                <ButtonLink to={to(`gestion/impayes/${i.case.id}`)} variant="secondaire" small>
                  Ouvrir le dossier
                </ButtonLink>
              ) : can("operate") ? (
                <Button small variant="secondaire" busy={busy === i.lease_id} onClick={() => run(i.lease_id, () => post(g("/debts"), { lease_id: i.lease_id }), "Dossier d’impayé ouvert.")}>
                  Créer un dossier
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {can("operate") ? (
          <div className="form-row">
            <LeaseSelect leases={leases.data?.items ?? []} value={lease} onChange={setLease} label="Ouvrir un dossier pour un autre bail" />
            <Button variant="secondaire" disabled={!lease} busy={busy === "open"} onClick={() => run("open", () => post(g("/debts"), { lease_id: lease }), "Dossier d’impayé ouvert.")}>
              Ouvrir
            </Button>
          </div>
        ) : null}
      </Card>
      {(q.data?.closed ?? []).length ? (
        <Card title="Dossiers clos">
          <ul className="gestion-list">
            {q.data!.closed.map((c) => (
              <li key={c.id} className="gestion-line">
                <span>Ouvert le {date(c.opened_on)}, clos le {date(c.closed_on)}</span>
                <Link to={to(`gestion/impayes/${c.id}`)}>Consulter</Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </>
  );
}

const INSTALLMENT: Record<string, string> = { reglee: "Réglée", partielle: "Partiellement réglée", en_retard: "En retard", a_venir: "À venir" };

export function ImpayePage() {
  const { caseId = "" } = useParams();
  const { g, can, to } = useG();
  const q = useGet<any>(g(`/debts/${caseId}`));
  const { busy, run } = useAction();
  const [plan, setPlan] = useState({ installments: "3", first_due_on: "" });
  const [accept, setAccept] = useState({ accepted_on: today(), note: "" });
  const [note, setNote] = useState("");
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error ?? new Error("Dossier introuvable.")} />;
  const d = q.data;
  const p = d.plan;
  const open = d.case.status !== "clos";
  return (
    <div className="page">
      <PageHeader
        eyebrow="Dossier d’impayé"
        title={d.lease.property_label}
        intro={`${d.tenants} · ouvert le ${date(d.case.opened_on)} · référence ${d.lease.reference}`}
        actions={
          <div className="gestion-actions">
            <a className="bv-btn bv-btn-secondaire" href={g(`/debts/${caseId}/export`)} target="_blank" rel="noreferrer">
              Exporter le dossier (PDF)
            </a>
            <ButtonLink to={to("gestion/relances")} variant="discret">
              Retour
            </ButtonLink>
          </div>
        }
      />
      <div className="stats">
        <div className="stat">
          <span className="stat-label">Reste dû échu</span>
          <span className="stat-value">{money(d.balance.overdue_cents)}</span>
          <span className="stat-hint">Recalculé à partir des échéances et paiements</span>
        </div>
        <div className="stat">
          <span className="stat-label">À échoir</span>
          <span className="stat-value">{money(d.balance.upcoming_cents)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Crédit non affecté</span>
          <span className="stat-value">{money(d.balance.credit_cents)}</span>
        </div>
      </div>
      <Card title="Échéances en retard">
        {d.overdue_calls.length === 0 ? <p className="muted">Aucune échéance en retard.</p> : null}
        <Table caption="Échéances en retard">
          <tbody>
            {d.overdue_calls.map((c: any) => (
              <tr key={c.id}>
                <td>{c.period_label}</td>
                <td>{date(c.due_on)}</td>
                <td className="right">
                  <Amount cents={c.outstanding_cents} />
                </td>
                <td>
                  <CallStatus status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card title="Échéancier amiable">
        {p ? (
          <>
            <KeyValues
              items={[
                ["Montant", money(p.plan.total_cents)],
                ["Mensualités", String(p.plan.installments)],
                ["Statut", p.plan.status === "accepte" ? `Accepté le ${date(p.plan.accepted_on)} (${p.plan.acceptance_note})` : p.plan.status === "propose" ? "Proposé, en attente d’accord" : p.plan.status],
                ["Situation", p.situation === "respecte" ? "Respecté" : p.situation === "en_retard" ? "Mensualité en retard" : p.situation === "termine" ? "Terminé" : p.situation],
                ["Réglé depuis l’accord", money(p.paid_since_cents)],
              ]}
            />
            <Table caption="Mensualités">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Date</th>
                  <th className="right">Montant</th>
                  <th className="right">Réglé</th>
                  <th>Situation</th>
                </tr>
              </thead>
              <tbody>
                {p.installments.map((i: any) => (
                  <tr key={i.seq}>
                    <td>{i.seq}</td>
                    <td>{date(i.due_on)}</td>
                    <td className="right">
                      <Amount cents={i.amount_cents} />
                    </td>
                    <td className="right">
                      <Amount cents={i.paid_cents} />
                    </td>
                    <td>{i.status === "en_retard" ? <Badge tone="erreur">En retard</Badge> : INSTALLMENT[i.status]}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {can("operate") && p.plan.status === "propose" ? (
              <div className="form-row">
                <Input label="Date de l’accord" type="date" max={today()} value={accept.accepted_on} onChange={(e) => setAccept({ ...accept, accepted_on: e.target.value })} />
                <Input label="Trace de l’accord du locataire" value={accept.note} onChange={(e) => setAccept({ ...accept, note: e.target.value })} placeholder="Ex. : accord écrit par courriel du 12/09" />
                <Button busy={busy === "accept"} disabled={!accept.note.trim()} onClick={() => run("accept", () => post(g(`/debts/${caseId}/plans/${p.plan.id}/accept`), accept), "Échéancier accepté : les relances sont suspendues pour ces échéances.")}>
                  Enregistrer l’accord
                </Button>
              </div>
            ) : null}
            {can("operate") && (p.plan.status === "accepte" || p.plan.status === "propose") ? (
              <div className="gestion-actions">
                <Button variant="discret" small busy={busy === "rompu"} onClick={() => run("rompu", () => post(g(`/debts/${caseId}/plans/${p.plan.id}/end`), { status: p.plan.status === "accepte" ? "rompu" : "annule" }), "Échéancier clos.")}>
                  {p.plan.status === "accepte" ? "Constater le non-respect" : "Retirer la proposition"}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="muted">Aucun échéancier en cours.</p>
        )}
        {can("operate") && open && (!p || p.plan.status !== "accepte") && d.balance.overdue_cents > 0 ? (
          <div className="form-row">
            <Input label="Nombre de mensualités" type="number" min={1} max={36} value={plan.installments} onChange={(e) => setPlan({ ...plan, installments: e.target.value })} />
            <Input label="Première mensualité le" type="date" min={today()} value={plan.first_due_on} onChange={(e) => setPlan({ ...plan, first_due_on: e.target.value })} />
            <Button
              variant="secondaire"
              disabled={!plan.first_due_on}
              busy={busy === "plan"}
              onClick={() => run("plan", () => post(g(`/debts/${caseId}/plans`), { installments: Number(plan.installments), first_due_on: plan.first_due_on }), "Échéancier proposé ; un courrier de proposition est prêt dans « Courriers ».")}
            >
              Proposer un échéancier
            </Button>
          </div>
        ) : null}
      </Card>
      <Card title="Garanties">
        <p>{d.guarantors.length ? `Garant(s) au bail : ${d.guarantors.join(", ")}.` : "Aucun garant enregistré sur ce bail."}</p>
        <Notice>{d.guarantee_notice}</Notice>
      </Card>
      <Card title="Chronologie">
        {can("operate") && open ? (
          <div className="form-row">
            <Input label="Ajouter une note ou un échange" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button
              variant="secondaire"
              disabled={!note.trim()}
              busy={busy === "note"}
              onClick={async () => {
                const ok = await run("note", () => post(g(`/debts/${caseId}/events`), { kind: "echange", text: note }), "Ajouté à la chronologie.");
                if (ok) setNote("");
              }}
            >
              Ajouter
            </Button>
          </div>
        ) : null}
        <ul className="gestion-list">
          {d.events.map((e: any) => (
            <li key={e.id}>
              <span className="small muted">{dateTime(e.created_at)}</span> — {e.text}
            </li>
          ))}
          {d.reminders.map((r: any) => (
            <li key={r.id}>
              <span className="small muted">{dateTime(r.sent_at ?? r.created_at)}</span> — {KIND_LABELS[r.kind] ?? r.kind} ({R_STATUS[r.status]?.label ?? r.status}) pour {date(r.period_start)}
            </li>
          ))}
          {d.letters.map((l: any) => (
            <li key={l.id}>
              <span className="small muted">{l.sent_at ? dateTime(l.sent_at) : "Non envoyé"}</span> — Courrier « {l.subject} » <Link to={to(`gestion/courriers?courrier=${l.id}`)}>voir</Link>
            </li>
          ))}
        </ul>
      </Card>
      {can("operate") && open ? (
        <Button variant="discret" busy={busy === "close"} onClick={() => run("close", () => post(g(`/debts/${caseId}/close`), { note: "Dossier clos par le bailleur." }), "Dossier clos.")}>
          Clore le dossier
        </Button>
      ) : null}
    </div>
  );
}

function InsuranceOverview() {
  const { g, can, to } = useG();
  const q = useGet<{ items: any[]; notice: string }>(g("/insurance"));
  const { busy, run } = useAction();
  return (
    <Card
      title="Assurance habitation des locataires"
      actions={
        can("operate") ? (
          <Button small busy={busy === "req"} onClick={() => run("req", () => post<any>(g("/insurance/requests")), (r: any) => `${r.sent} demande(s) d’attestation envoyée(s) ; ${r.skipped} déjà relancée(s) récemment ou sans adresse.`)}>
            Demander les attestations manquantes ou à renouveler
          </Button>
        ) : null
      }
    >
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? <Notice>{q.data.notice}</Notice> : null}
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun bail en cours" /> : null}
      <ul className="gestion-list">
        {(q.data?.items ?? []).map((i) => (
          <li key={i.lease_id} className="gestion-line">
            <span>
              <strong>{i.property_label}</strong> · {i.tenants}
              <span className="block small muted">
                {i.coverage.until ? `Période couverte jusqu’au ${date(i.coverage.until)}` : "Aucune période couverte enregistrée"}
                {i.last_request_at ? ` · dernière demande le ${dateTime(i.last_request_at)}` : ""}
              </span>
            </span>
            <span className="gestion-actions">
              <CoverageStatus state={i.coverage.state} label={i.coverage.label} />
              <Link to={to(`baux/${i.lease_id}?onglet=x-gestion-assurance`)}>Voir le bail</Link>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
